/**
 * Keep the HTML written in source mode as the public value of the editor.
 *
 * Tiptap/ProseMirror must parse HTML into its document schema before it can be
 * edited visually. Serialising that document is intentionally canonical and
 * may add wrappers (notably <p> inside table cells), change equivalent tags or
 * reorder attributes. This tiny state holder prevents that internal render
 * representation from replacing author-provided source until the user really
 * changes the visual document.
 */
export function toHtmlString(value) {
  return typeof value === "string" ? value : "";
}

export function createHtmlFidelityState(initialHtml = "") {
  let html = toHtmlString(initialHtml);

  return {
    get value() {
      return html;
    },

    matches(value) {
      return html === toHtmlString(value);
    },

    acceptSource(value) {
      html = toHtmlString(value);
      return html;
    },

    acceptVisual(value) {
      html = toHtmlString(value);
      return html;
    },

    acceptExternal(value) {
      html = toHtmlString(value);
      return html;
    }
  };
}

/**
 * Apply source HTML to the visual renderer while publishing the untouched
 * source string. `render` may internally canonicalise it; its return value is
 * deliberately ignored.
 */
export function commitSourceHtml(state, value, { render, emit }) {
  const html = state.acceptSource(value);
  render(html);
  emit(html);
  return html;
}

/** Do not reparse the editor when a v-model parent only echoes our value. */
export function applyExternalHtml(state, value, render) {
  if (state.matches(value)) return false;

  const html = state.acceptExternal(value);
  render(html);
  return true;
}

/** Convert a numeric height to px while still allowing any valid CSS length. */
export function toCssSize(value, fallback = "320px") {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? `${value}px` : fallback;
  }

  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  return fallback;
}
